'use strict';

import client from './Client';
import { find, get, has } from 'lodash';
import Logger from '../utils/Logger';
import ApiError from './ApiError';
import SessionUtils from '../utils/SessionUtils';
import WindowUtils from '../utils/WindowUtils';
import { setLastActive } from '../data/session/actions';
import { getTime } from '../utils/TimeUtils';

const { id: module_id } = module;
const logger = Logger(module_id);

const createApiError = (response) => {
  let error = new ApiError({
    code: response.data.errorCode,
    msg: response.data.message,
    url: get(response, 'request.responseURL'),
  });
  logger.error(error);
  return error;
};

function allowedHeaders(headers) {
  return (
    headers['content-type'].includes('application/json') ||
    headers['content-type'].includes('text/javascript')
  );
}

function callApi(call, isUserAction = true, dispatch) {
  return new Promise((resolve, reject) => {
    call
      .then((response) => {
        logger.debug(
          'response content type: ',
          response.headers['content-type']
        );

        if (!allowedHeaders(response.headers)) {
          throw new Error(
            'Response has invalid headers (not application/json or text/javascript on IE)'
          );
        }
        // if we encounter a 302 redirect, we are being redirected to idp and need to logout

        if (response.status === 302) {
          SessionUtils.logout({ internalLogoutReason: 'SESSION_TIMEOUT' });
        }

        if (!has(response, 'data')) {
          throw new Error('Response has invalid json');
        } else {
          if (response.data.status === 'SUCCESS') {
            if (isUserAction) {
              dispatch(setLastActive(getTime()));
            }
            resolve(response.data.payload);
          } else if (response.data.status === 'ERROR') {
            logger.error.apply(
              logger,
              [response.data.errorCode, response.data.message].filter(
                (item) => !!item
              )
            );
            reject(createApiError(response));
          }
        }
      })
      .catch((err) => {
        let { response } = err;

        // if we encounter 401, that means the user's state is desynced
        if (response && response.status === 401) {
          WindowUtils.refresh();
          // Special case, resolve so that logout is not called
          // in SessionTimer.
          // This should maybe have its own error type.
          resolve();
        }
        if (response && response.data) {
          // if we encounter response with data, we have a response
          // coming from backend. This is an allowed case.
          reject(createApiError(response));
        } else {
          // this should not happen but reject anyway
          reject();
        }
      });
  });
}
const HPA_ENABLED_HEADER = 'X-Hpa-Session';

let GET = 'GET';
let POST = 'POST';
let PUT = 'PUT';
let DELETE = 'DELETE';

const postBase = {
  dataType: 'json',
  contentType: 'application/json',
  method: 'POST',
};

const putBase = {
  dataType: 'json',
  contentType: 'application/json',
  method: 'PUT',
};

const getBase = {
  dataType: 'json',
  contentType: 'application/json',
  method: 'GET',
};

const deleteBase = {
  dataType: 'json',
  contentType: 'application/json',
  method: 'DELETE',
};

const postMethod = (options) => {
  return { ...postBase, ...options };
};

const putMethod = (options) => {
  return { ...putBase, ...options };
};

const deleteMethod = (options) => {
  return { ...deleteBase, ...options };
};

const getMethod = (options) => {
  return options;
};

const methods = {
  GET: getMethod,
  POST: postMethod,
  PUT: putMethod,
  DELETE: deleteMethod,
};

const hasOwnProperty = (obj, path) =>
  Object.prototype.hasOwnProperty.call(obj, path);

const endpoints = [
  {
    name: 'saveUserAgreement',
    method: POST,
  },
  {
    name: 'saveUserTosAgreement',
    method: POST,
  },
  {
    name: 'getPatientServiceIntegrations',
    method: GET,
  },
  {
    name: 'getLibreView',
    method: GET,
  },
  {
    name: 'updateLibreView',
    method: POST,
  },
  {
    name: 'libreviewRedirect',
    path: 'libreview/libreviewRedirect',
    method: GET,
  },
  {
    name: 'setContactList',
    method: POST,
  },
  {
    name: 'updateContact',
    path: 'contacts',
    method: POST,
  },
  {
    name: 'setCloseContact',
    path: 'contacts/{contactId}/closestContact',
    method: PUT,
  },
  {
    name: 'getHpaContact',
    path: 'hpaContact',
    method: GET,
  },
  {
    name: 'updateHpaContact',
    path: 'hpaContact',
    method: POST,
  },
  {
    name: 'isFirstTimeHpaSession',
    method: GET,
  },
  {
    name: 'updateFirstTimeHpaSession',
    method: GET,
  },
  {
    name: 'hasPreviousHpaSessions',
    method: GET,
  },
  {
    name: 'removeHpaContact',
    path: 'hpaContact/removeHpaContact',
    method: DELETE,
  },
  {
    name: 'removeContact',
    path: 'contacts/{contactId}',
    method: DELETE,
  },
  {
    name: 'deleteContact',
    method: POST,
  },
  {
    name: 'getAssignedForms',
    path: 'form/getAssignedForms',
    method: GET,
  },
  {
    name: 'getAllCompletedAivaProcesses',
    path: 'form/aiva/getAllCompletedProcesses',
    method: GET,
  },
  {
    name: 'getAivaProcess',
    path: 'form/aiva/{aivaProcessId}',
    method: GET,
  },
  {
    name: 'updateFormAssignment',
    path: 'form/assignment/update',
    method: PUT,
  },
  {
    name: 'getQuestionnaires',
    path: 'questionnaires',
    method: GET,
  },
  {
    name: 'assignQuestionnaire',
    method: POST,
  },
  {
    name: 'assignQuestionnaireOt',
    path: 'assignQuestionnaireOt/{formIdRevLatest}/{formTitle}',
    method: POST,
  },
  {
    name: 'getFormData',
    path: 'formdata/{id}',
    method: GET,
  },
  {
    name: 'dismissActivateServiceNotification',
    method: POST,
  },
  {
    name: 'requestAccount',
    method: POST,
  },
  {
    name: 'requestPassword',
    method: POST,
  },
  {
    name: 'getNotifications',
    method: GET,
  },
  {
    name: 'dismissNotifications',
    method: POST,
  },
  {
    name: 'getGlobalNotifications',
    method: GET,
  },
  {
    name: 'init',
    method: GET,
  },
  {
    name: 'getHPA',
    method: GET,
  },
  {
    name: 'exitHpa',
    method: POST,
  },
  {
    name: 'getFimlabLaboratories',
    method: GET,
  },
  {
    name: 'apiLogout',
    method: POST,
  },
  {
    name: 'updateUserAgreement',
    method: POST,
  },
  {
    name: 'getPatientContactDetails',
    method: GET,
  },
  {
    name: 'updatePhoneInfo',
    method: POST,
  },
  {
    name: 'updateEmailInfo',
    method: POST,
  },
  {
    name: 'getEmailInfo',
    method: GET,
  },
  {
    name: 'getUserInfo',
    path: 'userInfo',
    method: GET,
  },
  {
    name: 'dismissUpdateRequestNotification',
    method: POST,
  },
  {
    name: 'getTrustedContext',
    method: GET,
  },
  {
    name: 'denyUserAgreementOnRegistration',
    method: POST,
  },
  /*

  IOTAYS-726 Remove guides - getGuides call removed

  {
    name: "getGuides",
    method: GET
  },
  */
  {
    name: 'getTimelineAndQuestionnaires',
    method: GET,
  },
  {
    name: 'getTimelineEvents',
    method: GET,
  },
  {
    name: 'approveTimelineEvent',
    method: POST,
  },
  {
    name: 'getGenericPhrases',
    method: GET,
  },
  {
    name: 'setReferralNotified',
    method: POST,
  },
  {
    name: 'getAppointments',
    method: GET,
  },
  {
    name: 'getQueueAppointments',
    method: GET,
  },
  {
    name: 'getAvailableSlots',
    method: POST,
  },
  {
    name: 'getAppointmentRescheduleOptions',
    method: POST,
  },
  {
    name: 'getAppointmentReservationOptions',
    method: GET,
  },
  {
    name: 'getTimesForClinic',
    path: 'getTimesForClinic/{clinicId}',
    method: GET,
  },
  {
    name: 'getTimesForMaternityClinic',
    path: 'getTimesForMaternityClinic/{clinicId}/{ultraType}/{dueDate}',
    method: GET,
  },
  {
    name: 'reserveAppointment',
    method: POST,
  },
  {
    name: 'rescheduleAppointment',
    method: POST,
  },
  {
    name: 'validatePinCode',
    method: POST,
  },
  {
    name: 'keepAlive',
    method: GET,
    isUserAction: false,
  },
  {
    name: 'gtiredirect',
    method: GET,
  },
  {
    name: 'taysPolkuRedirect',
    path: 'tayspolku/taysPolkuRedirect',
    method: GET,
  },
  {
    name: 'coxaPolkuRedirect',
    path: 'coxapolku/coxaPolkuRedirect',
    method: GET,
  },
  {
    name: 'getLibreviewLoginUrl',
    path: 'libreview/getLoginUrl',
    method: GET,
  },
  {
    name: 'libreviewAuthentication',
    path: 'libreview/authentication',
    method: POST,
  },
  {
    name: 'getLinkedValue',
    path: 'getLink/{codeSetName}/{keys}',
    method: GET,
  },
  {
    name: 'getToken',
    method: GET,
  },
  {
    name: 'omavointiRedirect',
    method: POST,
  },
  {
    name: 'getCareConversationMessageUnreadCount',
    path: 'careconversation/message-unread-count',
    method: GET,
    isUserAction: false,
  },
  {
    name: 'getCareConversationLink',
    path: 'careconversation/get-careconversation-link',
    method: GET,
    isUserAction: false,
  },
  {
    name: 'getVideoConferenceLink',
    path: 'videoconference/get-videoconference-link',
    method: 'GET',
  },
  {
    name: 'getConsentStatus',
    method: POST,
  },
  {
    name: 'consentUpdate',
    method: POST,
  },
  {
    name: 'registration',
    method: POST,
  },
  {
    name: 'documentContent',
    queryParams: ['documentType', 'documentVersion'],
    method: GET,
  },
  {
    name: 'updateBioBankAgreement',
    method: POST,
  },
  {
    name: 'getEpisodeOfCare',
    path: 'EpisodeOfCare',
    method: GET,
  },
  {
    name: 'fetchPostOffice',
    method: GET,
    path: 'contacts/fetchPostOffice/{postalCode}',
  },
  {
    name: 'getTexts',
    method: GET,
  },
  {
    name: 'getSuomiFiViestitStatus',
    path: 'viranomaispalvelut/hasActiveInbox',
    method: GET,
  },
  {
    name: 'getUserInfoRequests',
    path: 'userInfoRequests',
    method: GET,
  },
  {
    name: 'userInfoRequestsHealthCheck',
    path: 'userInfoRequests/healthCheck',
    method: GET,
  },
  {
    name: 'userInfoRequestsSave',
    path: 'userInfoRequests/{id}',
    method: POST,
  },
  {
    name: 'userInfoRequestLogEvent',
    path: 'userInfoRequests/logEvent',
    method: POST,
  },
  {
    name: 'getAccessToken',
    path: 'getAccessToken',
    isUserAction: false,
    method: GET,
  },
];

class Api {
  constructor() {
    this._paths = [];
  }

  createMethod(endpoint) {
    let path = endpoint.path || endpoint.name;
    let method = methods[endpoint.method];
    let isUserAction = endpoint.isUserAction;
    let hasPathVariables = false;

    if (endpoint.path) {
      // matches two path variables
      let regex = new RegExp('({[a-zA-Z]+})');
      let pathVariables = regex.exec(endpoint.path);
      if (pathVariables) {
        hasPathVariables = true;
      }
    }

    return (options) => {
      let callPath = path;

      // expand path variables
      if (hasPathVariables) {
        callPath = path.replace(/{([^{}]*)}/g, (a, b) => {
          return options.data[b];
        });
      }

      // expand query parameters if they are provided
      if (endpoint.queryParams) {
        let queryParams = [];

        endpoint.queryParams.map((name) => {
          if (options.data[name]) {
            queryParams.push(name + '=' + options.data[name]);
          }
        });

        if (queryParams) {
          callPath = callPath + '?' + queryParams.join('&');
        }
      }
      return callApi(
        client(callPath, method(options)),
        isUserAction,
        this._store.dispatch
      );
    };
  }

  extendPaths(pathExtensions) {
    let pathExtensionNames = pathExtensions.map((path) => path.name);
    this._paths = this._paths.map((path) => {
      if (pathExtensionNames.indexOf(path.name)) {
        return pathExtensions[pathExtensionNames.indexOf(path.name)];
      }

      return path;
    });
  }

  set paths(paths) {
    this.initializePaths(paths);
  }

  initializePaths(paths) {
    this._paths = paths.map((path) => {
      let { name, useHpa, isUserAction } = path;
      let initializedPath = {
        key: name,
        call: this.createMethod(path),
        options: {},
      };

      if (useHpa !== undefined) {
        initializedPath.options.useHpa = useHpa;
      }
      if (isUserAction !== undefined) {
        initializedPath.options.isUserAction = isUserAction;
      }
      return initializedPath;
    });
  }

  set store(store) {
    this._store = store;
  }

  getHpaState() {
    let state = this._store.getState();
    return state.hpa && state.hpa.isHpa ? state.hpa.hpaSessionId : null;
  }

  createMethods() {
    this._paths.forEach((path) => {
      Object.defineProperty(this, path.key, {
        value: this.doCall(path.key),
        enumerable: true,
      });
    });
  }

  doCall = (path) => (payload) => {
    // get api path configuration.
    let pathConfig = find(this._paths, (p) => p.key === path);
    let headers = {};

    // default value for useHpa is true
    let useHpa = hasOwnProperty(pathConfig.options, 'useHpa')
      ? pathConfig.options.useHpa
      : true;

    if (useHpa) {
      let hpaState = this.getHpaState();
      if (hpaState) {
        headers[HPA_ENABLED_HEADER] = hpaState;
      }
    }

    return pathConfig.call({
      data: payload,
      headers,
    });
  };
}

const api = new Api();

api.paths = endpoints;

export default api;
